import {THREE_LETTER_CODES, TWO_LETTER_CODES} from './isoLanguageCodes';

// A language tag in the shape that `lang` accepts: a primary subtag of two or
// three letters, and then any number of subtags for the script, the region and
// the variant. This is the shape that `language_tag` in `pinecast/i18n.py`
// checks. This file also checks that the language is one that exists.
const LANGUAGE_TAG = /^[a-z]{2,3}(-[a-z0-9]{1,8})*$/i;

const decode = (base64: string): Uint8Array =>
  Uint8Array.from(atob(base64), character => character.charCodeAt(0));

const twoLetterCodes = decode(TWO_LETTER_CODES);
const threeLetterCodes = decode(THREE_LETTER_CODES);

/**
 * Whether `primary`, the first subtag of a tag, is the code of a language: a
 * code of ISO 639 (two letters of ISO 639-1, three letters of ISO 639-3) that the
 * registry of language subtags lists, or one of its special codes (`und`, `mul`,
 * `mis`, `zxx`).
 *
 * `us`, `xx` and `qqq` have the shape of a language, and they are not one. A
 * page with such a `lang` fails WCAG 3.1.1, and a screen reader does not know
 * which voice to use. The list is in the code (`isoLanguageCodes.ts`). It does
 * not depend on the names that the runtime knows: `Intl.DisplayNames` has a name
 * for about one code in nine. It has none for Levantine Arabic (`apc`),
 * Mesopotamian Arabic (`acm`) or Southern Pashto (`pbt`), and a podcast in such
 * a language got the language of the page.
 */
export function isLanguageCode(primary: string): boolean {
  if (!/^[a-z]{2,3}$/i.test(primary)) {
    return false;
  }
  const index = Array.from(primary.toLowerCase()).reduce(
    (total, character) => total * 26 + character.charCodeAt(0) - 97,
    0,
  );
  const bitmap = primary.length === 2 ? twoLetterCodes : threeLetterCodes;
  return Boolean(bitmap[index >> 3] & (1 << (index & 7)));
}

/**
 * `language` as a language tag that a page can have in `lang`, or `null` if it
 * has none.
 *
 * A podcast's `language` is what the podcaster saved or what the `<language>` of
 * the imported feed said, so it can be "English", "en_US", "us" or a space. The
 * separator is repaired (`pt_BR` becomes `pt-BR`), and the tag is written in the
 * canonical form: the case of the subtags, and the shortest code for a language
 * (`fra` becomes `fr`, which is also the language that the catalogs have under
 * that name). All other text that is not the tag of a language is dropped and
 * not guessed at.
 */
export const resolveLanguageTag = (
  language: string | null | undefined,
): string | null => {
  const tag = (language || '').trim().replace(/_/g, '-');
  if (!LANGUAGE_TAG.test(tag)) {
    return null;
  }
  let canonical: string;
  try {
    // A tag that has the shape of a tag and is not one, such as a subtag that is
    // repeated, throws.
    [canonical] = Intl.getCanonicalLocales(tag);
  } catch {
    return null;
  }
  return canonical && isLanguageCode(canonical.split('-')[0])
    ? canonical
    : null;
};
